﻿<script lang="ts" setup>
    import { reactive, inject } from 'vue';
    import { type Contact } from './Contact.access';
    import { type List } from './Contact.access';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import AddedLists from '@shared/components/List.vue';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { vTooltip } from '@shared/directives';
    import TagsList from '@shared/components/Tags/TagsList.vue';
    import { type TagsProvide, type Tag } from '@shared/components/Tags/Tags.access';

    const tags = inject('tags') as TagsProvide;

    const props = defineProps<{
        contact: Contact;
        lists: List[];
        isBusy: boolean;
    }>();

    const state = reactive<{
        selectedIds: string[];
    }>({
        selectedIds: [],
    });

    const emit = defineEmits(['update:selectedItems']);

    const filterOptions = () => {
        return ['[Name]', '[Description]'];
    };

    const orderOptions = () => {
        return [
            { display: $Resources.smsSortByLastModifiedLabel, key: 'LastModified' },
            { display: $Resources.CreatedLabel, key: 'Created' },
            { display: $Resources.NameLabel, key: 'Name' },
            { display: $Resources.DescriptionLabel, key: 'Description' },
        ];
    };

    const clearSelection = () => {
        state.selectedIds = [];
    };

    const removeSelectedItems = () => {
        let newVal = props.contact.Lists.filter((list: List) => {
            return !state.selectedIds.includes(list.ListId);
        });

        emit('update:selectedItems', newVal);
        clearSelection();
    };

    const filteredLists = () => {
        let result: List[] = [];

        props.contact.Lists.forEach((addedList: List) => {
            let found = props.lists.find((list: List) => {
                return list.ListId === addedList.ListId;
            });

            found && result.push(found);
        });

        return result;
    };
</script>

<script lang="ts"></script>

<template>
    <h2 class="h4">{{ $Resources.ContactModalListTabHeading }}</h2>

    <MultiSelect
        class="form-group"
        :loading="props.isBusy"
        :items="props.lists"
        :selectedItems="props.contact.Lists"
        :label="$Resources.AddContactToList"
        :placeholder="$Resources.AddContactToListHelp"
        :hideAdded="true"
        displayProp="Name"
        selectProp="ListId"
        @update:selectedItems="(newVal) => $emit('update:selectedItems', newVal)"></MultiSelect>

    <AddedLists
        itemId="ListId"
        :items="filteredLists()"
        :checkedItems="state.selectedIds"
        :loading="props.isBusy"
        :orderOptions="orderOptions()"
        :filterKeys="filterOptions()"
        :tags="tags"
        @update:checkedItems="(newVal) => (state.selectedIds = newVal as string[])"
        @clearSelected="clearSelection"
        @deleteSelected="removeSelectedItems">
        <template #item="{ item, index }">
            <div>
                <h3 class="h4">
                    {{ item.Name }}
                    <a class="small normal m-xs-l" :href="'/Lists/' + item.ListId" rel="noopener" target="_blank" v-tooltip.top="$Resources.GotToListLabel">
                        <span class="glyphicon glyphicon-new-window" aria-hidden="true"></span>
                    </a>
                </h3>

                <p v-show="item.Description">{{ item.Description }}</p>

                <TagsList
                    v-show="item.Tags.length > 0"
                    :tags="item.Tags"
                    :selectedIds="tags.state.selectedIds"
                    @toggleTag="(tag: Tag) => tags.toggle(tag)"></TagsList>

                <p class="meta">
                    <span class="glyphicon glyphicon-time"></span>
                    {{ $Resources.GenericCreatedLabel }}
                    <time :datetime="item.Created">{{ formatDateTime(item.Created) }}</time>
                    {{ $Resources.GenericByLabel }}
                    {{ item.CreatedBy.Name }}

                    <i class="fa fa-pencil" aria-hidden="true"></i>
                    {{ $Resources.LastModifiedLabel }}
                    <time :datetime="item.Modified">{{ formatDateTime(item.Modified) }}</time>
                    {{ $Resources.GenericByLabel }}
                    {{ item.ModifiedBy.Name }}
                </p>
            </div>
        </template>
    </AddedLists>
</template>

<style></style>
